import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Typography,
  CircularProgress,
  IconButton,
} from '@mui/material';
import { Close } from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { getImageUrl } from '../../../utils/imageHelper';

const CreatePlaylistModal = ({ open, onClose, onSave }) => {
  const { enqueueSnackbar } = useSnackbar();
  const [playlistData, setPlaylistData] = useState({
    title: '',
    image: null,
    description: '',
  });
  const [playlistTypeId, setPlaylistTypeId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingTypes, setLoadingTypes] = useState(true);

  useEffect(() => {
    if (open) {
      fetchPlaylistTypes();
      setPlaylistData({
        title: '',
        image: null,
        description: '',
      });
    }
  }, [open]);

  const fetchPlaylistTypes = async () => {
    setLoadingTypes(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/lookups/playlist`);
      if (response.ok) {
        const types = await response.json();
        // Find Private type
        const privateType = types.find(t => t.playlist_type_name === 'Private');
        if (privateType) {
          setPlaylistTypeId(privateType.playlist_type_lookup_id);
        } else {
          enqueueSnackbar('Private playlist type not found', { variant: 'error' });
        }
      }
    } catch (error) {
      console.error('Error fetching playlist types:', error);
      enqueueSnackbar('Failed to load playlist types', { variant: 'error' });
    } finally {
      setLoadingTypes(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setPlaylistData(prev => ({ ...prev, [name]: value }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setPlaylistData(prev => ({ ...prev, image: file }));
    }
  };

  const handleSave = async () => {
    if (!playlistData.title.trim()) {
      enqueueSnackbar('Please enter a playlist title', { variant: 'warning' });
      return;
    }

    if (!playlistTypeId) {
      enqueueSnackbar('Playlist type not loaded', { variant: 'error' });
      return;
    }

    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('title', playlistData.title);
      formData.append('description', playlistData.description || '');
      formData.append('playlist_type_lookup_id', playlistTypeId);
      formData.append('spotify_url', '');

      if (playlistData.image instanceof File) {
        formData.append('image', playlistData.image);
      }

      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/playlist`, {
        method: 'POST',
        body: formData,
      });

      if (response.ok) {
        const newPlaylist = await response.json();
        enqueueSnackbar('Playlist created successfully', { variant: 'success' });
        onSave(newPlaylist);
        handleClose();
      } else {
        const error = await response.json();
        throw new Error(error.error || 'Failed to create playlist');
      }
    } catch (error) {
      console.error('Error creating playlist:', error);
      enqueueSnackbar(error.message || 'Failed to create playlist', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setPlaylistData({
      title: '',
      image: null,
      description: '',
    });
    setLoading(false);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          bgcolor: '#1e1e1e',
          color: 'white',
        },
      }}
    >
      <DialogTitle sx={{ bgcolor: '#2c2c2c', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h6">Create Private Playlist</Typography>
        <IconButton onClick={handleClose} sx={{ color: 'white' }}>
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ bgcolor: '#121212' }}>
        {loadingTypes ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              label="Playlist Title"
              name="title"
              value={playlistData.title}
              onChange={handleInputChange}
              fullWidth
              required
              sx={{
                '& .MuiOutlinedInput-root': {
                  color: 'white',
                  '& fieldset': {
                    borderColor: '#666',
                  },
                  '&:hover fieldset': {
                    borderColor: '#888',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#4caf50',
                  },
                },
                '& .MuiInputLabel-root': {
                  color: '#999',
                },
              }}
            />

            <Typography variant="subtitle2" sx={{ color: '#999' }}>
              Upload Playlist Image (Optional)
            </Typography>
            <Button
              variant="outlined"
              component="label"
              sx={{
                borderColor: '#666',
                color: 'white',
                '&:hover': {
                  borderColor: '#888',
                },
              }}
            >
              Upload Image
              <input
                type="file"
                accept="image/*"
                hidden
                onChange={handleImageChange}
              />
            </Button>

            {playlistData.image && (
              <Box>
                <img
                  src={playlistData.image instanceof File ? URL.createObjectURL(playlistData.image) : getImageUrl(playlistData.image)}
                  alt="Preview"
                  style={{ width: '100%', maxWidth: 200, borderRadius: 8 }}
                />
              </Box>
            )}

            <TextField
              label="Description"
              name="description"
              value={playlistData.description}
              onChange={handleInputChange}
              fullWidth
              multiline
              minRows={3}
              sx={{
                '& .MuiOutlinedInput-root': {
                  color: 'white',
                  '& fieldset': {
                    borderColor: '#666',
                  },
                  '&:hover fieldset': {
                    borderColor: '#888',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#4caf50',
                  },
                },
                '& .MuiInputLabel-root': {
                  color: '#999',
                },
              }}
            />
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ bgcolor: '#2c2c2c', px: 3, py: 2 }}>
        <Button onClick={handleClose} disabled={loading} sx={{ color: 'white' }}>
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={loading || !playlistData.title.trim() || loadingTypes}
          sx={{
            bgcolor: '#4caf50',
            '&:hover': { bgcolor: '#45a049' },
          }}
        >
          {loading ? 'Creating...' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreatePlaylistModal;

